<template>
  <div>

    <div class="w-full flex flex-wrap mb-6">
      <div class="w-full md:w-1/4 lg:w-1/5 md:mr-8 mb-4">
        <div class="g--form__label uppercase text-teal mb-1">Yoga Type</div>
        <select v-model="currentYogaType" @change="filterChanged" class="g--select g--input">
          <option value="0">All</option>
          <option v-for="yogaType in yogaTypes" :value="yogaType.id">{{ yogaType.title }}</option>
        </select>
      </div>
      <div class="w-full md:w-1/4 lg:w-1/5 md:mr-8 mb-4">
        <div class="g--form__label uppercase text-teal mb-1">Teacher</div>
        <select v-model="currentTeacher" @change="filterChanged" class="g--select g--input">
          <option value="0">All</option>
          <option v-for="teacher in teachers" :value="teacher.id">{{ teacher.fullName }}</option>
        </select>
      </div>
      <div class="w-full md:w-1/4 lg:w-1/5 md:mr-8 mb-4">
        <div class="g--form__label uppercase text-teal mb-1">Date</div>
        <input
            type="date"
            v-model="startDate"
            :min="new Date().toISOString().split('T')[0]"
            @change="changeStartDate"
            class="g--input"
        >
      </div>
    </div>


    <!-- Add navigation controls -->
    <div class="flex flex-wrap justify-between items-center mb-4">
      <h2 class="sm:hidden w-full text-center mb-2">{{ formatDateRange() }}</h2>
      <button
          class="g--button"
          @click="getWeekDates(date)[6].setHours(0,0,0,0) <= new Date().setHours(0,0,0,0) ? null : previousWeek()"
          :disabled="getWeekDates(date)[6].setHours(0,0,0,0) <= new Date().setHours(0,0,0,0)"
          :class="{'opacity-50 cursor-not-allowed': getWeekDates(date)[6].setHours(0,0,0,0) <= new Date().setHours(0,0,0,0)}"
      >&lt; Previous
      </button>
      <h2 class="hidden sm:block">{{ formatDateRange() }}</h2>
      <button class="g--button" @click="nextWeek">Next &gt;</button>

    </div>

    <div class="grid grid-cols-4 sm:grid-cols-7 gap-x-4">
      <div
          v-for="(_, date) in weekDays"
          :key="date"
          @click="new Date(date).setHours(0, 0, 0, 0) < new Date().setHours(0,0,0,0) ? null : selectDay(date)"
          :class="{
            'bg-teal text-white hover:bg-teal': isSelectedDay(date),
            'opacity-50 cursor-not-allowed': isNotAllowedDay(date),
            'cursor-pointer': isAllowedDay(date),
            'hover:bg-teal-lightest': !isSelectedDay(date) && isAllowedDay(date)
          }"
          class="p-2 rounded-lg"
      >
        <h2 class="uppercase text-center tracking-wide font-medium text-base">
          <span class="hidden sm:block ">{{ formatDateWeekDay(date) }}</span>
          <span class="sm:hidden">{{ formatDateWeekDayShort(date) }}</span><br/>
          {{ formatDateDay(date) }}
          <div v-if="days[date] && days[date].length"
               :class="{'text-white': isSelectedDay(date)}"
               class="text-teal text-3xl leading-none mt-1">
            {{ days[date].length <= 2 ? '•' : days[date].length <= 6 ? '••' : '•••' }}
          </div>

        </h2>
      </div>
    </div>

    <div class="mt-4" v-if="selectedDaySessions.length && !loading">
      <div v-for="session in selectedDaySessions" :key="session.id">
        <SessionLine :logged-in="loggedIn" class="mb-6" :session="session"></SessionLine>
      </div>
    </div>
    <div class="mt-4 flex items-center justify-center" v-else-if="loading">
      <Spinner class="mt-10"></Spinner>
    </div>
    <div class="mt-4 text-center pt-4." v-else>
      <p>No classes found that day.<br/>

        <button v-if="currentYogaType !=0 || currentTeacher != 0" class="mt-2 g--button" @click="resetFilters">Reset the
          filters
        </button>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed , defineProps} from 'vue';

import SessionLine from './SessionLine.vue';
import Spinner from './Spinner.vue';

const props = defineProps({
  loggedIn : {
    default: false,
    type: Boolean
  },
  teacherId: {
    default: 0,
    type: Number
  }
});

const days = ref([]);

const date = ref(new Date());

const loading = ref(false);

const yogaTypes = ref([]);
const currentYogaType = ref(0);
const currentTeacher = ref(0);
const teachers = ref([]);
const startDate = ref(new Date().toISOString().split('T')[0]);

onMounted(() => {
  
  currentTeacher.value = props.teacherId;

  // Initialize date and selectedDate based on startDate
  date.value = new Date(startDate.value);
  selectedDate.value = startDate.value;
  console.log(currentTeacher.value);
  fetchClasses();
});

const dateChanged = () => {
  console.log(date.value);
}

const filterChanged = () => {
  // Remove the date reset, only fetch new classes with updated filters
  fetchClasses();
  console.log(yogaTypes.value);
}
const resetFilters = () => {
  currentYogaType.value = 0;
  currentTeacher.value = props.teacherId;
  startDate.value = new Date().toISOString().split('T')[0];
  selectedDate.value = startDate.value;
  date.value = new Date(startDate.value);
  fetchClasses();
}

const fetchClasses = () => {
  loading.value = true;
  fetch(`/api/sessions/all?groupByDay=1&yogaTypeId=${currentYogaType.value}&teacherId=${currentTeacher.value}&hidePrivate=1`, {
    method: 'get',
    headers: {
      'Content-type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        console.log(response);
        days.value = response.sessions;
        yogaTypes.value = response.yogaTypes;
        teachers.value = response.teachers;
        loading.value = false;

        // Find the first day of the week that has classes
        const weekDates = Object.keys(weekDays.value);
        const firstDayWithClasses = weekDates.find(date => days.value[date]?.length > 0);
        
        // If we found a day with classes, set it as selected
        if (firstDayWithClasses) {
          selectedDate.value = firstDayWithClasses;
        }
      });
}

// Add new date handling functions
const getWeekDates = (currentDate) => {
  const start = new Date(currentDate);
  // If today is Sunday (0), we want to go back 6 days to get to last Monday
  // For other days, we go back to the previous Monday
  const daysToSubtract = start.getDay() === 0 ? 6 : start.getDay() - 1;
  start.setDate(start.getDate() - daysToSubtract); // Start of week (Monday)

  const dates = [];
  for (let i = 0; i < 7; i++) { // Get 7 days (Mon-Sun)
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    dates.push(day);
  }
  return dates;
};

const weekDays = computed(() => {
  const weekDates = getWeekDates(date.value);
  const dates = {};

  weekDates.forEach(weekDate => {
    const dateStr = weekDate.toISOString().split('T')[0];
    dates[dateStr] = true;
  });

  return dates;
});

const selectedDate = ref(new Date().toISOString().split('T')[0]);

const selectedDaySessions = computed(() => {
  return days.value[selectedDate.value] || [];
});

const selectDay = (dateStr) => {
  selectedDate.value = dateStr;
};

const isSelectedDay = (dateStr) => {
  return selectedDate.value === dateStr;
};

const isNotAllowedDay = (dateStr) => {
  return new Date(dateStr).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0);
}

const isAllowedDay = (dateStr) => {
  return new Date(dateStr).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0);
}

const formatDateWeekDay = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-US', {weekday: 'long'});
};
const formatDateWeekDayShort = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-US', {weekday: 'short'});
};
const formatDateDay = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-US', {month: 'short', day: 'numeric'});
};

const formatDateRange = () => {
  const weekDates = getWeekDates(date.value);
  const start = weekDates[0];
  const end = weekDates[6];
  return `${start.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})} - ${end.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}`;
};

const previousWeek = () => {
  date.value = new Date(date.value.setDate(date.value.getDate() - 7));
  const weekStart = getWeekDates(date.value)[0];
  selectedDate.value = weekStart.toISOString().split('T')[0];
  fetchClasses();
};

const nextWeek = () => {
  date.value = new Date(date.value.setDate(date.value.getDate() + 7));
  const weekStart = getWeekDates(date.value)[0];
  selectedDate.value = weekStart.toISOString().split('T')[0];
  fetchClasses();
};

const changeStartDate = () => {
  date.value = startDate.value;
  selectedDate.value = new Date(startDate.value).toISOString().split('T')[0];
  // fetchClasses();
};
</script>